import type { KickerPart, TableDefinition, TargetPart } from './definition';

export interface CatchPlacement {
  id: string;
  x?: number;
  y?: number;
  scoreValue?: number;
  holdMs?: number;
  radius?: number;
  ejectVelocity?: KickerPart['ejectVelocity'];
  randomEject?: boolean;
  scoreBoostSeconds?: number;
  scoreBoostMultiplier?: number;
  material?: string;
  color?: string;
  soundRef?: string;
  artRef?: string;
}

type CatchProfile = Omit<CatchPlacement, 'id' | 'x' | 'y' | 'scoreValue' | 'radius'>;

/**
 * Built-in catches should be memorable mechanisms, not the same scoop with a
 * different cabinet around it. These profiles keep the proven kicker runtime
 * while changing tempo, release direction, sound and visual language per
 * machine. Explicit placement options still win, so individual tables remain
 * free to tune a catch without changing this shared identity layer.
 */
const BUILT_IN_CATCH_PROFILES: Partial<Record<string, CatchProfile>> = {
  'grand-circuit-v1': {
    holdMs: 1350,
    ejectVelocity: { x: 38, y: -22 },
    randomEject: false,
    scoreBoostSeconds: 0,
    scoreBoostMultiplier: 1,
    color: '#37d9ff',
    soundRef: 'pit-service',
    artRef: 'badge-pinball',
  },
  'candy-carnival-v1': {
    holdMs: 1050,
    ejectVelocity: { x: 24, y: -26 },
    randomEject: true,
    scoreBoostSeconds: 14,
    scoreBoostMultiplier: 3,
    color: '#ff78c8',
    soundRef: 'slide-whistle',
    artRef: 'pin-paw',
  },
  'cosmic-relay-v1': {
    holdMs: 320,
    ejectVelocity: { x: 46, y: -44 },
    randomEject: false,
    scoreBoostSeconds: 6,
    scoreBoostMultiplier: 4,
    color: '#8b7dff',
    soundRef: 'target-zap',
    artRef: 'planet-ring',
  },
  'neon-rush-v1': {
    holdMs: 220,
    ejectVelocity: { x: -10, y: -58 },
    randomEject: false,
    scoreBoostSeconds: 4,
    scoreBoostMultiplier: 5,
    color: '#53e4ff',
    soundRef: 'ramp-rise',
    artRef: 'reactor-ring',
  },
  'haunted-keep-v1': {
    ejectVelocity: { x: 20, y: -32 },
    randomEject: false,
    scoreBoostSeconds: 9,
    scoreBoostMultiplier: 3,
    color: '#8fc7b5',
    soundRef: 'target-zap',
    artRef: 'ghost-eye',
  },
  'jungle-temple-v1': {
    ejectVelocity: { x: 30, y: -34 },
    randomEject: false,
    scoreBoostSeconds: 18,
    scoreBoostMultiplier: 2,
    color: '#e0b94e',
    soundRef: 'target-knock',
    artRef: 'crown',
  },
  'deep-sea-dive-v1': {
    ejectVelocity: { x: 18, y: -20 },
    randomEject: true,
    scoreBoostSeconds: 7,
    scoreBoostMultiplier: 4,
    color: '#42d9d0',
    soundRef: 'pop-deep',
    artRef: 'wave-mark',
  },
  'clockwork-district-v1': {
    ejectVelocity: { x: -34, y: -38 },
    randomEject: false,
    scoreBoostSeconds: 12,
    scoreBoostMultiplier: 3,
    color: '#d79a45',
    soundRef: 'coil-heavy',
    artRef: 'circuit-node',
  },
  'frontier-run-v1': {
    ejectVelocity: { x: 36, y: -30 },
    randomEject: false,
    scoreBoostSeconds: 8,
    scoreBoostMultiplier: 3,
    color: '#e8a24c',
    soundRef: 'steam-brake',
    artRef: 'starburst',
  },
  'dragon-keep-v1': {
    ejectVelocity: { x: 0, y: -52 },
    randomEject: false,
    scoreBoostSeconds: 5,
    scoreBoostMultiplier: 3,
    color: '#ff6b3d',
    soundRef: 'jackpot-fanfare',
    artRef: 'crown',
  },
};

function applyGrandCircuitPitService(table: TableDefinition): void {
  if (table.id !== 'grand-circuit-v1') return;

  const finishSpecs = [
    { id: 'target-1', width: 7.2, scoreValue: 700, destroyScoreValue: 900 },
    { id: 'target-2', width: 5.4, scoreValue: 1100, destroyScoreValue: 1700 },
    { id: 'target-3', width: 3.6, scoreValue: 1900, destroyScoreValue: 3200 },
  ] as const;

  // Pit Service releases all three sector markers together, but they are no
  // longer interchangeable. Sector 1 is the broad, low-risk braking zone;
  // Sector 2 is the balanced chicane; Sector 3 is a narrow, high-value speed
  // trap. Grand therefore keeps its simultaneous-bank identity while creating
  // a visible risk/reward choice instead of converging on sequential target play.
  for (const spec of finishSpecs) {
    const target = table.parts.find(
      (part): part is TargetPart => part.id === spec.id && part.kind === 'target',
    );
    if (!target) continue;
    target.appearsAfterPartId = 'catch-grand';
    target.dropDown = true;
    target.hitPoints = 1;
    target.width = spec.width;
    target.scoreValue = spec.scoreValue;
    target.destroyScoreValue = spec.destroyScoreValue;
  }

  // Grand's left orbit used to be visually readable but mechanically empty.
  // Two non-colliding timing gates turn it into a qualifying line: the entry
  // gate is broad and modest, while the apex is narrower and pays materially
  // more. Inject them here instead of into GRAND_CIRCUIT itself because every
  // themed table clones that shared reference hardware before receiving its
  // own identity layer.
  if (!table.parts.some((part) => part.id === 'grand-qualifying-gate-entry')) {
    table.parts.push(
      {
        id: 'grand-qualifying-gate-entry',
        kind: 'rollover',
        scoreValue: 700,
        center: { x: 8.48, y: 54.66 },
        radius: 1.5,
        laneId: 'grand-qualifying-orbit',
        color: '#ff3eaa',
        soundRef: 'lane-bleep',
        artRef: 'circuit-node',
      },
      {
        id: 'grand-qualifying-gate-apex',
        kind: 'rollover',
        scoreValue: 1600,
        center: { x: 11.38, y: 27.88 },
        radius: 1.15,
        laneId: 'grand-qualifying-orbit',
        color: '#37d9ff',
        soundRef: 'lane-bleep',
        artRef: 'circuit-node',
      },
    );
  }
}

function applyCandySugarJackpot(table: TableDefinition): void {
  if (table.id !== 'candy-carnival-v1') return;

  const leftPrize = table.parts.find(
    (part): part is TargetPart => part.id === 'target-1' && part.kind === 'target',
  );
  const jackpot = table.parts.find(
    (part): part is TargetPart => part.id === 'target-2' && part.kind === 'target',
  );
  const rightPrize = table.parts.find(
    (part): part is TargetPart => part.id === 'target-3' && part.kind === 'target',
  );
  if (!jackpot) return;

  // The candy scoop starts a small prize cascade instead of acting as an
  // isolated multiplier: catch the ball to reveal the center Sugar Jackpot,
  // crack it, then the two side prizes physically open for cleanup shots.
  jackpot.appearsAfterPartId = 'catch-candy';
  jackpot.dropDown = true;
  jackpot.hitPoints = 1;
  jackpot.scoreValue = 1800;
  jackpot.destroyScoreValue = 5200;

  // The two side prizes deliberately ask for different shots after the jackpot.
  // Left is the bulky carnival plush: lower-value contacts but two hits to knock
  // it down. Right is the golden-ticket shot: a single, higher-value precision
  // hit. Both still appear together and both are required by the existing prize
  // mission, so the table gains route choice without changing proven geometry.
  if (leftPrize) {
    leftPrize.appearsAfterPartId = 'target-2';
    leftPrize.dropDown = true;
    leftPrize.hitPoints = 2;
    leftPrize.scoreValue = 850;
    leftPrize.destroyScoreValue = 2200;
  }
  if (rightPrize) {
    rightPrize.appearsAfterPartId = 'target-2';
    rightPrize.dropDown = true;
    rightPrize.hitPoints = 1;
    rightPrize.scoreValue = 1700;
    rightPrize.destroyScoreValue = 3600;
  }

  const sugarLanes = [
    { id: 'roll-a', scoreValue: 650, scoreBoostSeconds: 6 },
    { id: 'roll-b', scoreValue: 950, scoreBoostSeconds: 4.5 },
    { id: 'roll-c', scoreValue: 1400, scoreBoostSeconds: 3 },
  ] as const;

  // Candy already owns a long catch-driven 3x prize run. These upper lanes are
  // deliberately smaller reloads instead of another jackpot window: route the
  // ball upstairs between prize shots to earn a brief 2x burst. The safer lane
  // gives more time, while the shorter windows pay more immediately, creating a
  // carnival risk/reward loop without copying the asymmetric-pop identities on
  // Deep Sea, Clockwork, or Neon.
  for (const spec of sugarLanes) {
    const lane = table.parts.find((part) => part.id === spec.id);
    if (lane?.kind !== 'rollover') continue;
    lane.scoreValue = spec.scoreValue;
    lane.scoreBoostSeconds = spec.scoreBoostSeconds;
    lane.scoreBoostMultiplier = 2;
  }
}

function applyCosmicRelayChain(table: TableDefinition): void {
  if (table.id !== 'cosmic-relay-v1') return;

  const specs = [
    { id: 'target-1', scoreValue: 900, destroyScoreValue: 1200, appearsAfterPartId: 'catch-cosmic' },
    { id: 'target-2', scoreValue: 1300, destroyScoreValue: 2200, appearsAfterPartId: 'target-1' },
    { id: 'target-3', scoreValue: 1800, destroyScoreValue: 3600, appearsAfterPartId: 'target-2' },
  ] as const;

  for (const spec of specs) {
    const node = table.parts.find(
      (part): part is TargetPart => part.id === spec.id && part.kind === 'target',
    );
    if (!node) continue;
    node.dropDown = true;
    node.hitPoints = 1;
    node.scoreValue = spec.scoreValue;
    node.destroyScoreValue = spec.destroyScoreValue;
    node.appearsAfterPartId = spec.appearsAfterPartId;
  }

  if (!table.parts.some((part) => part.id === 'cosmic-orbit-deflector-left')) {
    const { width, height } = table.board;
    table.parts.push(
      {
        id: 'cosmic-orbit-deflector-left',
        kind: 'guide-rail',
        scoreValue: 0,
        points: [
          { x: width * 0.1, y: height * 0.36 },
          { x: width * 0.22, y: height * 0.25 },
        ],
        thickness: 1,
        collider: true,
        material: 'metal',
        color: '#8b7dff',
        soundRef: 'lane-bleep',
        artRef: 'planet-ring',
      },
      {
        id: 'cosmic-orbit-deflector-right',
        kind: 'guide-rail',
        scoreValue: 0,
        points: [
          { x: width * 0.9, y: height * 0.36 },
          { x: width * 0.78, y: height * 0.25 },
        ],
        thickness: 1,
        collider: true,
        material: 'metal',
        color: '#8b7dff',
        soundRef: 'lane-bleep',
        artRef: 'planet-ring',
      },
    );
  }
}

function applyNeonReactorStates(table: TableDefinition): void {
  if (table.id !== 'neon-rush-v1') return;

  const specs = [
    { id: 'bumper-left', radius: 3.1, impulse: 52, scoreValue: 1150 },
    { id: 'bumper-center', radius: 2.7, impulse: 98, scoreValue: 2100 },
    { id: 'bumper-right', radius: 2.9, impulse: 74, scoreValue: 800 },
  ] as const;

  // Neon is a narrow speed table, so its reactor should be a routing decision
  // instead of three interchangeable pops. The left coolant coil is the broad,
  // low-energy control shot; the center ignition core is deliberately violent
  // and pays the most; the right discharge coil keeps velocity high for the
  // ramp/breaker transition but pays less. Geometry remains proven and only the
  // contact profiles change.
  for (const spec of specs) {
    const reactor = table.parts.find((part) => part.id === spec.id);
    if (reactor?.kind !== 'bumper') continue;
    reactor.radius = spec.radius;
    reactor.impulse = spec.impulse;
    reactor.scoreValue = spec.scoreValue;
  }
}

function applyJungleRelicChamber(table: TableDefinition): void {
  if (table.id !== 'jungle-temple-v1') return;

  const relic = table.parts.find(
    (part): part is TargetPart => part.id === 'target-2' && part.kind === 'target',
  );
  const stoneGuardian = table.parts.find(
    (part): part is TargetPart => part.id === 'target-1' && part.kind === 'target',
  );
  const gargoyleGuardian = table.parts.find(
    (part): part is TargetPart => part.id === 'target-3' && part.kind === 'target',
  );
  if (!relic) return;

  // The center crystal is the treasure, not just another guardian. Keep it
  // physically sealed until the long relic catch is captured, then make the
  // player crack the two-hit chamber while the catch's sustained 2x hunt is live.
  relic.appearsAfterPartId = 'catch-jungle';
  relic.hitPoints = 2;
  relic.scoreValue = 1400;
  relic.destroyScoreValue = 4200;

  // Shattering the relic still releases both guardians together, preserving
  // Jungle's choose-your-threat arena. The stone guardian is now a low-value
  // armored attrition fight, while the gargoyle is a fragile high-value strike.
  // That makes target order and surviving the 18-second 2x hunt meaningful
  // without copying Haunted Keep's sequential boss ladder.
  if (stoneGuardian) {
    stoneGuardian.hitPoints = 4;
    stoneGuardian.scoreValue = 650;
    stoneGuardian.destroyScoreValue = 2600;
  }
  if (gargoyleGuardian) {
    gargoyleGuardian.hitPoints = 1;
    gargoyleGuardian.scoreValue = 1800;
    gargoyleGuardian.destroyScoreValue = 4200;
  }
}

function applyDeepSeaPressureLock(table: TableDefinition): void {
  if (table.id !== 'deep-sea-dive-v1') return;

  const pressureLock = table.parts.find(
    (part): part is TargetPart => part.id === 'target-2' && part.kind === 'target',
  );
  if (!pressureLock) return;

  // Deep Sea's long catch now changes the available shot structure: the center
  // pressure lock is physically sealed until the sub is held in the deep catch.
  // The revealed lock is a single deliberate break shot with a meaningful award,
  // giving the slow table a calm capture -> breach rhythm before the trench ramp.
  pressureLock.appearsAfterPartId = 'catch-deep';
  pressureLock.dropDown = true;
  pressureLock.hitPoints = 1;
  pressureLock.scoreValue = 1500;
  pressureLock.destroyScoreValue = 4500;
}

function applyFrontierTrainRobbery(table: TableDefinition): void {
  if (table.id !== 'frontier-run-v1') return;

  const robberyStages = [
    { id: 'target-1', scoreValue: 1100, destroyScoreValue: 1600, appearsAfterPartId: 'catch-frontier' },
    { id: 'target-2', scoreValue: 1500, destroyScoreValue: 2400, appearsAfterPartId: 'target-1' },
    { id: 'target-3', scoreValue: 2200, destroyScoreValue: 3800, appearsAfterPartId: 'target-2' },
  ] as const;

  // Frontier's catch now boards the train and starts a timed 3x robbery run.
  // One outlaw target is exposed at a time, turning the old flat three-target
  // bank into a readable catch -> lock -> safe -> marshal shot sequence.
  for (const stage of robberyStages) {
    const target = table.parts.find(
      (part): part is TargetPart => part.id === stage.id && part.kind === 'target',
    );
    if (!target) continue;
    target.appearsAfterPartId = stage.appearsAfterPartId;
    target.dropDown = true;
    target.hitPoints = 1;
    target.scoreValue = stage.scoreValue;
    target.destroyScoreValue = stage.destroyScoreValue;
  }

  // The gold-seam objective should feel like drilling volatile ore rather than
  // farming a third ordinary pop. Shrink the center mine's contact window, make
  // its rebound materially more violent, and pay accordingly. The side bumpers
  // remain the calmer railway scenery, so the three required mine hits become a
  // deliberate risk/reward commitment without changing table geometry.
  const mine = table.parts.find((part) => part.id === 'bumper-center');
  if (mine?.kind === 'bumper') {
    mine.radius = 2.55;
    mine.impulse = 86;
    mine.scoreValue = 1800;
  }
}

/**
 * Adds a visual/physics catch cup that briefly holds the ball, then ejects it.
 * Creator/unknown tables preserve the original safe random scoop defaults;
 * official machines receive the themed profile above unless a caller overrides
 * an individual value.
 */
export function addCatchBonus(table: TableDefinition, placement: CatchPlacement): TableDefinition {
  if (table.parts.some((part) => part.id === placement.id)) return table;
  applyGrandCircuitPitService(table);
  applyCandySugarJackpot(table);
  applyCosmicRelayChain(table);
  applyNeonReactorStates(table);
  applyJungleRelicChamber(table);
  applyDeepSeaPressureLock(table);
  applyFrontierTrainRobbery(table);
  const profile = BUILT_IN_CATCH_PROFILES[table.id];
  const resolved = { ...profile, ...placement };
  const kicker: KickerPart = {
    id: placement.id,
    kind: 'kicker',
    scoreValue: placement.scoreValue ?? 750,
    center: {
      x: placement.x ?? table.board.width * 0.25,
      y: placement.y ?? table.board.height * 0.15,
    },
    holdMs: resolved.holdMs ?? 700,
    ejectVelocity: resolved.ejectVelocity ?? { x: 24, y: -28 },
    radius: placement.radius ?? 2.7,
    randomEject: resolved.randomEject ?? true,
    scoreBoostSeconds: resolved.scoreBoostSeconds ?? 10,
    scoreBoostMultiplier: resolved.scoreBoostMultiplier ?? 2,
    material: resolved.material ?? 'metal',
    color: resolved.color ?? '#ffd84a',
    soundRef: resolved.soundRef ?? 'cat-meow',
    artRef: resolved.artRef ?? 'pin-moon',
  };
  table.parts.push(kicker);
  return table;
}